// src/features/distributionBoard/DistributionBoardInspector.jsx
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { openModal }           from '../../../../../redux/reducers/global/modal/modalSlice';
import { buildDistributionBoard } from '../../../../../redux/reducers/global/distributionBoard/distributionBoardSlice';
import { setNodeAttributes, setNodeHideAttributeLabels } from '../../../../../redux/reducers/global/node/nodeSlice';
import { setDistributionBoardPreviewEditNodeId } from '../../../../../redux/reducers/global/view/viewSlice';
import visibleIcon from '../../menuBar/assets/ico-visible.svg';
import hiddenIcon from '../../menuBar/assets/ico-hidden.svg';
import EditableInspectorLabel from '../EditableInspectorLabel';
import './style.css';

const FolderIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="distribution-board-inspector__button-icon">
    <path d="M3.5 7.5h6l1.8 2h9.2v8.75a2 2 0 0 1-2 2h-15v-12.75a2 2 0 0 1 2-2h3.4" />
    <path d="M3.5 10h17" />
  </svg>
);

const MoveIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="distribution-board-inspector__button-icon">
    <path d="M12 2.75v18.5" />
    <path d="m8 6.75 4-4 4 4" />
    <path d="m8 17.25 4 4 4-4" />
    <path d="M2.75 12h18.5" />
    <path d="m6.75 8-4 4 4 4" />
    <path d="m17.25 8 4 4-4 4" />
  </svg>
);

const InfoIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="distribution-board-inspector__hint-icon">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 10.75v5.5" />
    <path d="M12 7.5h.01" />
  </svg>
);

const InspectorSection = ({ label, children }) => (
  <section className="distribution-board-inspector__section">
    <div className="distribution-board-inspector__section-title">
      <span>{label}</span>
      <i aria-hidden="true" />
    </div>
    {children}
  </section>
);

const DistributionBoardInspector = () => {
  const dispatch = useDispatch();
  const selectedNodeId = useSelector((state) => state.node.selectedNodeId);
  const currentNode = useSelector((state) => (
    state.node.nodes.find((node) => node.id === state.node.selectedNodeId) || null
  ));
  const previewEditNodeId = useSelector((state) => state.view.distributionBoardPreviewEditNodeId);
  const attrs = currentNode?.data?.attributes || {};
  const nombreAttr = attrs.nombre || {};
  const hideParameterLabelsDefault = useSelector((state) => !!state.view.hideParameterLabelsDefault);
  const hideParameterLabels = typeof currentNode?.data?.hideAttributeLabels === 'boolean'
    ? currentNode.data.hideAttributeLabels
    : hideParameterLabelsDefault;
  const nameValue = nombreAttr.value ?? '';
  const nameIsVisible = nombreAttr.visibilityUserModified === true ? !!nombreAttr.isVisible : true;
  const previewEnabled = attrs.showDistributionBoardPreview?.value === true;
  const previewScaleRaw = Number(attrs.distributionBoardPreviewScale?.value);
  const previewScaleBase = Number.isFinite(previewScaleRaw) && previewScaleRaw > 0 ? previewScaleRaw : 1;
  const previewScale = Math.min(2, Math.max(1, previewScaleBase));
  const isPreviewMoveEditing = previewEditNodeId === selectedNodeId;
  const previewPercent = Math.round(previewScale * 100);

  const openDistributionBoard = () => {
    dispatch(buildDistributionBoard());

   // (If you want to control the modal separately, you can still do this:
    dispatch(openModal({
      modalType: 'OPEN_DISTRIBUTION_BOARD',
      modalProps: {}
    }));
  }

  const handleHideLabelsToggle = (event) => {
    if (!selectedNodeId) return;
    dispatch(setNodeHideAttributeLabels({
      nodeId: selectedNodeId,
      hideAttributeLabels: event.target.checked,
    }));
  };

  const handleNameChange = (event) => {
    if (!selectedNodeId) return;
    dispatch(setNodeAttributes({
      nodeId: selectedNodeId,
      attributes: {
        nombre: {
          ...nombreAttr,
          value: event.target.value,
          userModified: true,
        },
      },
    }));
  };

  const handleNameLabelCommit = (nextLabel) => {
    if (!selectedNodeId) return;
    dispatch(setNodeAttributes({
      nodeId: selectedNodeId,
      attributes: {
        nombre: {
          ...nombreAttr,
          label: nextLabel || 'Nombre',
        },
      },
    }));
  };

  const handleNameVisibilityToggle = () => {
    if (!selectedNodeId) return;
    dispatch(setNodeAttributes({
      nodeId: selectedNodeId,
      attributes: {
        nombre: {
          ...nombreAttr,
          isVisible: !nameIsVisible,
          visibilityUserModified: true,
        },
      },
    }));
  };

  const handlePreviewToggle = (event) => {
    if (!selectedNodeId) return;
    const nextValue = !!event.target.checked;
    dispatch(setNodeAttributes({
      nodeId: selectedNodeId,
      attributes: {
        showDistributionBoardPreview: {
          ...(attrs.showDistributionBoardPreview || {}),
          value: nextValue,
        },
      },
    }));
    if (!nextValue && isPreviewMoveEditing) {
      dispatch(setDistributionBoardPreviewEditNodeId(null));
    }
  };

  const handlePreviewScaleChange = (event) => {
    if (!selectedNodeId) return;
    const nextValue = Number(event.target.value);
    dispatch(setNodeAttributes({
      nodeId: selectedNodeId,
      attributes: {
        distributionBoardPreviewScale: {
          ...(attrs.distributionBoardPreviewScale || {}),
          value: nextValue,
        },
      },
    }));
  };

  const handlePreviewMoveToggle = () => {
    dispatch(setDistributionBoardPreviewEditNodeId(isPreviewMoveEditing ? null : selectedNodeId));
  };

  return (
    <div className="distribution-board-inspector">
      <div className="distribution-board-inspector__header">
        <div className="distribution-board-inspector__heading">
          <div className="inspector__title">Centro de cargas</div>
        </div>

      </div>

      <div className="inspector__divider" />

      <label className="distribution-board-inspector__label-toggle">
        <input
          type="checkbox"
          checked={hideParameterLabels}
          onChange={handleHideLabelsToggle}
        />
        <span>Ocultar nombres de los parámetros</span>
      </label>

      <InspectorSection label="Parámetros">
        <div className="inspector__field distribution-board-inspector__field">
          <EditableInspectorLabel
            label={nombreAttr.label}
            defaultLabel="Nombre"
            onCommit={handleNameLabelCommit}
          />
          <div className="inspector__field-controls">
            <div className="inspector__field-input">
              <input
                className="inspector__field-input-element"
                type="text"
                value={nameValue}
                onChange={handleNameChange}
                placeholder={nombreAttr.label || 'Nombre'}
              />
            </div>
            <button
              type="button"
              className="inspector__field-visibility-toggle"
              onClick={handleNameVisibilityToggle}
              title={nameIsVisible ? 'Ocultar del plano' : 'Mostrar en el plano'}
            >
              <img
                className="inspector__field-visibility-icon"
                src={nameIsVisible ? visibleIcon : hiddenIcon}
                alt={nameIsVisible ? 'Visible en plano' : 'Oculto en plano'}
              />
            </button>
          </div>
        </div>
      </InspectorSection>

      <InspectorSection label="Visualización">
        <label className="distribution-board-inspector__toggle">
          <input
            type="checkbox"
            checked={previewEnabled}
            onChange={handlePreviewToggle}
          />
          <span>Mostrar centro de carga</span>
        </label>

        {previewEnabled ? (
          <>
            <label className="distribution-board-inspector__range">
              <span>Tamaño</span>
              <strong>{previewPercent}%</strong>
              <input
                type="range"
                min="1"
                max="2"
                step="0.05"
                value={previewScale}
                onChange={handlePreviewScaleChange}
              />
            </label>

            <button
              type="button"
              className="distribution-board-inspector__action"
              onClick={openDistributionBoard}
            >
              <FolderIcon />
              <span>Abrir panel</span>
            </button>
          </>
        ) : null}
      </InspectorSection>

      {previewEnabled ? (
        <InspectorSection label="Posición">
          <button
            type="button"
            className={`distribution-board-inspector__action${isPreviewMoveEditing ? ' is-active' : ''}`}
            onClick={handlePreviewMoveToggle}
          >
            <MoveIcon />
            <span>{isPreviewMoveEditing ? 'Mover centro de carga: activo' : 'Mover centro de carga'}</span>
          </button>

          <div className="distribution-board-inspector__hint">
            <InfoIcon />
            <span>Ajusta la posición arrastrando el centro de carga en el diagrama.</span>
          </div>
        </InspectorSection>
      ) : (
        <div className="distribution-board-inspector__preview-off">
          <button
            type="button"
            className="distribution-board-inspector__action"
            onClick={openDistributionBoard}
          >
            <FolderIcon />
            <span>Abrir panel</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default DistributionBoardInspector;
